import React, { useState, useMemo } from 'react';
import ApplicationSummary from './ApplicationSummary.js';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../services/firebaseConfig';
import { APPLICATION_STATUSES } from '../constants/crmConstants';

/**
 * Applications Component
 * FULL STABLE VERSION WITH FIREBASE STORAGE INTEGRATION
 * Features: 10-section creation flow with dynamic file uploads and multi-entry qualifications.
 */
const Applications = ({ user, applicants = [], settings, applications = [], onSaveApplication, viewOnly = false}) => {
  const [view, setView] = useState('list'); // 'list' or 'create'
  const [activeSection, setActiveSection] = useState(1);
  const [viewingApp, setViewingApp] = useState(null); 
  const [isSubmitting, setIsSubmitting] = useState(false); // Track upload/save progress
  const [currentDraftId, setCurrentDraftId] = useState(null);

  // Add near your other useState calls
  const [filters, setFilters] = useState({
    program: 'All Programs',
    intake: 'All Intakes',
    status: 'All Statuses',
    search: ''
  });

  const [form, setForm] = useState({
    // Section 2: Personal Details
    applicantId: '',
    firstName: '',
    lastName: '',
    dob: '',
    phone: '',
    gender: '',
    email: '',
    nationality: '',
    streetAddress: '',
    city: '',
    country: '',
    state: '',
    postcode: '',
    // Section 3: Program Selection
    programSelections: [
      { country: '', universityId: '', twgProgram: '', course: '', intake: '' }
    ],
    // Section 4: Parent / Guardian Information
    guardianGivenName: '',
    guardianFamilyName: '',
    guardianRelationship: '',
    guardianOccupation: '',
    guardianEmail: '',
    guardianPhone: '',
    guardianAddressSame: 'Yes',
    guardianStreetAddress: '',
    guardianCity: '',
    guardianCountry: '',
    guardianState: '',
    guardianPostcode: '',
    // Section 5: Academic Qualifications
    qualifications: [
      {
        highestQualification: '',
        educationBoard: '',
        highestQualificationCountry: '',
        institutionName: '',
        languageOfInstruction: '',
        isCompleted: 'No',
        completionDate: '',
        marksGpa: ''
      }
    ],
    academicDocuments: null,
    hasAcademicGap: 'No',
    gapReason: '',
    gapDocuments: null,
    // Section 6: English Proficiency
    hasEnglishTest: 'No',
    englishTestName: '',
    englishTestScore: '',
    englishTestVerificationId: '',
    englishTestResultDoc: null,
    englishProficiencyDescription: '',
    // Section 7: Financial Assessment
    primarySponsor: '',
    sponsorAnnualIncome: '',
    plannedLoanAmount: '',
    liquidFundsAmount: '',
    assetValue: '',
    fundingAdditionalDetails: '',
    relevantFinancialDocs: null,
    financialDeclaration: false,
    // Section 8: Immigration History
    visaRefusal: 'No',
    criminalHistory: 'No',
    nonCompliantRecord: 'No',
    overstayRecord: 'No',
    relativesInCountry: 'No',
    immigrationDetails: '',
    // Section 9: Documents
    section8PersonalDocs: null,
    section8AdmissionDocs: null,
    // Section 10: Declaration
    declarationAgree: false,
    privacyAgree: false,
    isAbove18: 'Yes',
    guardianSignature: '',
    partnerDeclarationAgree: false
  });

  const saveDraft = async (nextSection) => {
    const appId = currentDraftId || `APP-${Math.floor(10000 + Math.random() * 90000)}`;
    
    const draftData = {
      ...form,
      id: appId,
      status: 'Draft',
      agentId: user?.uid,
      applicantName: `${form.firstName} ${form.lastName}`.trim() || 'New Draft',
      lastSaved: new Date().toISOString()
    };

    // Clean up file objects for Firestore compatibility
    Object.keys(draftData).forEach(key => {
      if (
        draftData[key] instanceof FileList || 
        (Array.isArray(draftData[key]) && draftData[key][0] instanceof File) ||
        draftData[key] instanceof File
      ) {
        delete draftData[key];
      }
    });

    try {
      await onSaveApplication(draftData);
      if (!currentDraftId) setCurrentDraftId(appId);
      setActiveSection(nextSection);
    } catch (error) {
      console.error("Draft save failed", error);
    }
  };

  const sections = [
    { id: 1, title: 'Instructions' },
    { id: 2, title: 'Personal' },
    { id: 3, title: 'Programs' },
    { id: 4, title: 'Guardian' },
    { id: 5, title: 'Academic' },
    { id: 6, title: 'English' },
    { id: 7, title: 'Financial' },
    { id: 8, title: 'Immigration' },
    { id: 9, title: 'Documents' },
    { id: 10, title: 'Declaration' }
  ];

  // Helper: Qualification Update Logic
  const updateQualification = (idx, field, val) => {
    const updatedQuals = [...form.qualifications];
    updatedQuals[idx] = { ...updatedQuals[idx], [field]: val };
    setForm({ ...form, qualifications: updatedQuals });
  };

  const addQualification = () => {
    if (form.qualifications.length < 3) {
      setForm({
        ...form,
        qualifications: [
          ...form.qualifications,
          { 
            highestQualification: '', educationBoard: '', highestQualificationCountry: '', 
            institutionName: '', languageOfInstruction: '', isCompleted: 'No', 
            completionDate: '', marksGpa: '' 
          }
        ]
      });
    }
  };

  const removeQualification = (idx) => {
    const updatedQuals = form.qualifications.filter((_, i) => i !== idx);
    setForm({ ...form, qualifications: updatedQuals });
  };

  /**
   * Helper: Firebase Storage File Uploader
   */
  const uploadFiles = async (files, path) => {
    if (!files || files.length === 0) return []; // Return empty array if no files
  
    try {
      // Convert FileList to Array and map each file to an upload promise
      const uploadPromises = Array.from(files).map(async (file) => {
        const storageRef = ref(storage, `applications/${path}/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        return await getDownloadURL(snapshot.ref);
      });

      // Wait for all uploads to complete and return the array of URLs
      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error("File upload error:", error);
      return [];
    }
  };

  const handleApplicantSelect = (id) => {
    const applicant = applicants.find(a => String(a.id) === String(id));
    if (applicant) {
      setForm({
        ...form,
        applicantId: id,
        firstName: applicant.firstName,
        lastName: applicant.lastName,
        dob: applicant.dob,
        phone: applicant.phone || '',
        gender: applicant.gender,
        email: applicant.email,
        nationality: applicant.citizenship,
        streetAddress: applicant.address || ''
      });
    }
  };

  const updateProgramSelection = (idx, field, val) => {
    const sel = [...form.programSelections];
    if (field === 'country') {
      sel[idx] = { ...sel[idx], country: val, universityId: '', twgProgram: '', course: '', intake: '' };
    } else if (field === 'universityId') {
      sel[idx] = { ...sel[idx], universityId: val, twgProgram: '', course: '', intake: '' };
    } else if (field === 'twgProgram') {
      // When program changes, reset course and intake to prevent invalid combinations
      sel[idx] = { ...sel[idx], twgProgram: val, course: '', intake: '' };
    } else {
      sel[idx][field] = val;
    }
    setForm({ ...form, programSelections: sel });
  };

  const addProgramChoice = () => {
    if (form.programSelections.length < 3) {
      setForm({
        ...form,
        programSelections: [...form.programSelections, { country: '', universityId: '', twgProgram: '', course: '', intake: '' }]
      });
    }
  };

  const removeProgramChoice = (idx) => {
    const newSelections = form.programSelections.filter((_, i) => i !== idx);
    setForm({ ...form, programSelections: newSelections });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const section2MandatoryFields = [
      { key: 'applicantId', label: 'Applicant Selection' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'postcode', label: 'Postcode' },
      { key: 'country', label: 'Country' }
    ];

    const missingField = section2MandatoryFields.find(field => !form[field.key] || form[field.key].trim() === "");

    if (missingField) {
      alert(`Missing Information: Please provide the "${missingField.label}" in the Personal section (Section 2) before submitting.`);
      setActiveSection(2); // Redirect user to Section 2
      return;
    }

    const firstPref = form.programSelections[0];
    const isSection3Incomplete = !firstPref?.country || !firstPref?.universityId || 
                                 !firstPref?.twgProgram || !firstPref?.course || !firstPref?.intake;

    if (isSection3Incomplete) {
      alert("Missing Information: Please complete all fields for Preference 1 in the Program Selection (Section 3).");
      setActiveSection(3);
      return;
    }

    const section4MandatoryFields = [
      { key: 'guardianGivenName', label: 'Guardian Given Name' },
      { key: 'guardianFamilyName', label: 'Guardian Family Name' },
      { key: 'guardianRelationship', label: 'Guardian Relationship' },
      { key: 'guardianOccupation', label: 'Guardian Occupation' },
      { key: 'guardianEmail', label: 'Guardian Email' },
      { key: 'guardianPhone', label: 'Guardian Phone' }
    ];

    const missingSection4 = section4MandatoryFields.find(
      field => !form[field.key] || form[field.key].trim() === ""
    );

    if (missingSection4) {
      alert(`Missing Information: Please provide the "${missingSection4.label}" in the Guardian section (Section 4).`);
      setActiveSection(4);
      return;
    }

    // Also enforce the existing logic that guardian phone cannot match applicant phone
    if (form.guardianPhone === form.phone) {
      alert("Validation Error: Guardian phone number cannot be the same as the applicant's phone number.");
      setActiveSection(4);
      return;
    }

    const firstQual = form.qualifications[0];
    const section5MandatoryFields = [
      { key: 'highestQualification', label: 'Qualification Level' },
      { key: 'educationBoard', label: 'Education Board' },
      { key: 'highestQualificationCountry', label: 'Country of Completion' },
      { key: 'institutionName', label: 'School / Institution Name' },
      { key: 'languageOfInstruction', label: 'Language of Instruction' },
      { key: 'completionDate', label: 'Date of Completion' },
      { key: 'marksGpa', label: 'Marks / GPA' }
    ];

    const missingQualField = section5MandatoryFields.find(
      field => !firstQual?.[field.key] || String(firstQual[field.key]).trim() === ""
    );

    if (missingQualField) {
      alert(`Missing Information: Please provide the "${missingQualField.label}" for Qualification #1 in Section 5.`);
      setActiveSection(5);
      return;
    }

    const section7MandatoryFields = [
      { key: 'primarySponsor', label: 'Primary Sponsor' },
      { key: 'sponsorAnnualIncome', label: 'Annual Income' },
      { key: 'plannedLoanAmount', label: 'Planned Loan Amount' },
      { key: 'liquidFundsAmount', label: 'Liquid Funds Available' },
      { key: 'assetValue', label: 'Value of Immovable Assets' }
    ];

    const missingFinancialField = section7MandatoryFields.find(
      field => !form[field.key] || String(form[field.key]).trim() === ""
    );

    if (missingFinancialField) {
      alert(`Missing Information: Please provide the "${missingFinancialField.label}" in the Financial section (Section 7).`);
      setActiveSection(7);
      return;
    }

    // Ensure the Financial Declaration checkbox is also checked
    if (!form.financialDeclaration) {
      alert("Missing Information: Please accept the Financial Declaration in Section 7.");
      setActiveSection(7);
      return;
    }

    // Section 10: Mandatory Declarations Validation
    if (!form.declarationAgree || !form.privacyAgree || !form.partnerDeclarationAgree) {
      alert("Missing Information: You must accept all declarations and the privacy policy in Section 10 to submit.");
      setActiveSection(10);
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // 1. Upload multiple files and get arrays of URLs
      const personalDocUrls = await uploadFiles(form.section8PersonalDocs, `${form.email}/personal`);
      const admissionDocUrls = await uploadFiles(form.section8AdmissionDocs, `${form.email}/admission`);
      const academicDocUrls = await uploadFiles(form.academicDocuments, `${form.email}/academic`);
      const financialDocsUrls = await uploadFiles(form.relevantFinancialDocs, `${form.email}/financial`);
      const englishDocUrls = await uploadFiles(form.englishTestResultDoc, `${form.email}/english`);

      // Use the existing draft ID if available, otherwise create a new one
      const finalAppId = currentDraftId || `APP-${Math.floor(10000 + Math.random() * 90000)}`;
      const newApp = {
        ...form, 
        id: finalAppId,
        agentId: user?.uid,
        applicantName: `${form.firstName} ${form.lastName}`.trim(),
        status: 'App Submitted',
        dateApplied: new Date().toISOString().split('T')[0],
        
        personalDocUrls: personalDocUrls, 
        admissionDocUrls: admissionDocUrls,
        academicDocumentsUrls: academicDocUrls, 
        relevantFinancialDocsUrls: financialDocsUrls,
        englishTestResultUrls: englishDocUrls,

        twgProgram: form.programSelections[0]?.twgProgram || 'General Admission',
        intakeSummary: form.programSelections[0]?.intake || 'TBD',
        
        // Save the resulting arrays of URLs
        
      };
      
      // 2. Clean up raw FileList objects so Firestore accepts the data
      Object.keys(newApp).forEach(key => {
        if (
          newApp[key] instanceof FileList || 
          (Array.isArray(newApp[key]) && newApp[key][0] instanceof File) ||
          newApp[key] instanceof File
        ) {
          delete newApp[key];
        }
      });

      if (onSaveApplication) {
        await onSaveApplication(newApp);
      }
      
      setCurrentDraftId(null);
      // 3. Reset View and State
      setView('list');
      setActiveSection(1);
      setForm({
        applicantId: '', firstName: '', lastName: '', dob: '', phone: '', gender: '', email: '', nationality: '', streetAddress: '', city: '', country: '', state: '', postcode: '',
        programSelections: [{ country: '', universityId: '', twgProgram: '', course: '', intake: '' }],
        guardianGivenName: '', guardianFamilyName: '', guardianRelationship: '', guardianOccupation: '', guardianEmail: '', guardianPhone: '', guardianAddressSame: 'Yes', guardianStreetAddress: '', guardianCity: '', guardianCountry: '', guardianState: '', guardianPostcode: '',
        qualifications: [{ highestQualification: '', educationBoard: '', highestQualificationCountry: '', institutionName: '', languageOfInstruction: '', isCompleted: 'No', completionDate: '', marksGpa: '' }],
        academicDocuments: null, hasAcademicGap: 'No', gapReason: '', gapDocuments: null,
        hasEnglishTest: 'No', englishTestName: '', englishTestScore: '', englishTestVerificationId: '', englishTestResultDoc: null, englishProficiencyDescription: '',
        primarySponsor: '', sponsorAnnualIncome: '', plannedLoanAmount: '', liquidFundsAmount: '', assetValue: '', fundingAdditionalDetails: '', relevantFinancialDocs: null, financialDeclaration: false,
        visaRefusal: 'No', criminalHistory: 'No', nonCompliantRecord: 'No', overstayRecord: 'No', relativesInCountry: 'No', immigrationDetails: '',
        section8PersonalDocs: null, section8AdmissionDocs: null,
        declarationAgree: false, privacyAgree: false, isAbove18: 'Yes', guardianSignature: '', partnerDeclarationAgree: false
      });
    } catch (error) {
      console.error("Submission failed:", error);
      alert("System failed to submit application. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      // Extract program and intake (handling nested or summary fields)
      const appProgram = app.programSelections?.[0]?.twgProgram || app.twgProgram || app.programSummary || '';
      const appIntake = app.programSelections?.[0]?.intake || app.intakeSummary || '';
      
      // Check against each filter
      const matchProgram = filters.program === 'All Programs' || appProgram === filters.program;
      const matchIntake = filters.intake === 'All Intakes' || appIntake === filters.intake;
      const matchStatus = filters.status === 'All Statuses' || app.status === filters.status;
      const matchSearch = (app.applicantName || '').toLowerCase().includes(filters.search.toLowerCase()) || 
                          (app.id || '').toLowerCase().includes(filters.search.toLowerCase());

      return matchProgram && matchIntake && matchStatus && matchSearch;
    });
  }, [applications, filters]);

  const renderSection1 = () => (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="prose prose-slate max-w-none">
        <div className="bg-blue-50 border-l-4 border-[#0464AD] p-6 rounded-r-2xl mb-8">
          <h3 className="text-[#0464AD] font-black uppercase tracking-widest text-sm mb-2">Important Notice</h3>
          <p className="text-sm text-[#0464AD]/80 font-medium leading-relaxed">
            Please read carefully before proceeding. This application form contains 10 sections. All sections are compulsory. 
            Please allocate 30 to 45 minutes to fill this application form.
          </p>
        </div>

        <div className="space-y-6">
          <h3 className="text-lg font-black text-slate-800 uppercase tracking-tighter">Document Checklist</h3>
          <p className="text-sm text-slate-500 font-medium">Before starting, please make sure you have the following documents ready to upload:</p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 list-none p-0">
            {[
              "Valid Passport (or a valid government-issued photo ID)",
              "Recent passport-sized photograph",
              "Certified* copy of Class XII certificate",
              "Certified* copy of Class XII mark sheet",
              "Evidence of English language proficiency",
              "Certified* translation of any documents not in English",
              "Certified copy of undergraduate degree (for PG applicants)",
              "Certified copy of final year mark sheet (for PG applicants)"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                <span className="text-[#0464AD] mt-1">✓</span>
                <span className="text-xs font-bold text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 p-6 bg-slate-900 rounded-3xl text-white">
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-4">What is a "Certified Copy"?</h3>
          <p className="text-xs text-slate-300 leading-relaxed mb-4">
            A certified copy is signed by an authorised officer to acknowledge that it matches the original document exactly. Authorised officers include:
          </p>
          <ul className="text-xs text-slate-400 space-y-2 mb-6">
            <li>• Staff of the institution that issued the original document</li>
            <li>• Public notary</li>
            <li>• An authorised admissions counsellor or agent of The WorldGrad</li>
          </ul>
          <div className="flex items-center gap-2 text-[10px] font-black text-blue-400 uppercase tracking-widest bg-blue-500/10 p-3 rounded-xl border border-blue-500/20">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Upload limits: PDF, JPEG or PNG only | Size &lt; 1.5MB
          </div>
        </div>
      </div>
      <div className="flex justify-end pt-8">
        <button onClick={() => setActiveSection(2)} className="bg-[#0464AD] text-white px-10 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-[#0464AD]/20 hover:bg-[#0091D0] transition-all active:scale-95">
          Begin Application
        </button>
      </div>
    </div>
  );

  const renderSection2 = () => (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="space-y-4">
        <label className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
          Step 1: Link Applicant Profile
          <span className="text-[10px] bg-[#0464AD]/10 text-[#0464AD] px-2 py-0.5 rounded">AUTO-FILL ENABLED</span>
        </label>
        <select 
          className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]"
          value={form.applicantId}
          onChange={(e) => handleApplicantSelect(e.target.value)}
        >
          <option value="">Select an existing applicant...</option>
          {applicants.map(a => (
            <option key={a.id} value={a.id}>{a.firstName} {a.lastName} ({a.email})</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {['firstName', 'lastName', 'dob', 'gender', 'email', 'nationality'].map(field => (
          <div key={field} className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{field.replace(/([A-Z])/g, ' $1')}</label>
            <input type="text" readOnly className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-sm text-slate-500 outline-none" value={form[field]} />
          </div>
        ))}
      </div>
      <div className="h-px bg-slate-50" />
      <div className="space-y-6">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Residential Details (Manual Entry)</label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-3 space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Street Address</label>
            <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.streetAddress} onChange={e => setForm({...form, streetAddress: e.target.value})} />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">City</label>
            <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.city} onChange={e => setForm({...form, city: e.target.value})} />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">State</label>
            <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.state} onChange={e => setForm({...form, state: e.target.value})} />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Postcode</label>
            <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.postcode} onChange={e => setForm({...form, postcode: e.target.value})} />
          </div>
          <div className="lg:col-span-2 space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Country</label>
            <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.country} onChange={e => setForm({...form, country: e.target.value})} />
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-4 pt-4">
        <button onClick={() => setActiveSection(1)} className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-800">Back</button>
        <button onClick={() => saveDraft(3)} className="bg-[#0464AD] text-white px-10 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-[#0464AD]/20 hover:bg-[#0091D0] transition-all active:scale-95">Save & Continue</button>
      </div>
    </div>
  );

  const renderSection3 = () => (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter leading-none">Program Selection</h3>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Select up to 3 preferred university and program combinations</p>
        </div>
        {form.programSelections.length < 3 && (
          <button onClick={addProgramChoice} className="bg-slate-900 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase hover:bg-slate-800 transition-all shadow-lg">+ Add Preference</button>
        )}
      </div>

      <div className="space-y-8">
        {form.programSelections.map((selection, idx) => {
          const filteredUnis = (settings?.universities || []).filter(u => u.country === selection.country);
          const selectedUni = (settings?.universities || []).find(u => u.id === selection.universityId);
          const applicableTWGProgs = selectedUni?.applicablePrograms || [];

          const coursesObj = selectedUni?.courses || {};
          const selectedProgramKey = selection.twgProgram?.trim() || "";
          const applicableCourses = (coursesObj && typeof coursesObj === 'object' && !Array.isArray(coursesObj))
            ? (coursesObj[selectedProgramKey] || [])
            : [];

          const applicableIntakes = (settings?.intakeMappings?.[selection.twgProgram] || []);

          return (
            <div key={idx} className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 relative animate-in slide-in-from-left-4">
              <div className="absolute top-6 right-8 flex items-center gap-4">
                <span className="text-3xl font-black text-[#0464AD] uppercase tracking-tighter">Preference {idx + 1}</span>
                {form.programSelections.length > 1 && (
                  <button onClick={() => removeProgramChoice(idx)} className="text-red-400 hover:text-red-600 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeWidth="2.5"/></svg>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Destination Country</label>
                  <select className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={selection.country} onChange={(e) => updateProgramSelection(idx, 'country', e.target.value)}>
                    <option value="">Select Country...</option>
                    {(settings?.countries || []).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className={`space-y-1.5 transition-all ${!selection.country ? 'opacity-30 pointer-events-none grayscale' : ''}`}>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Destination University</label>
                  <select className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={selection.universityId} onChange={(e) => updateProgramSelection(idx, 'universityId', e.target.value)}>
                    <option value="">Select University...</option>
                    {filteredUnis.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>

                <div className={`space-y-1.5 transition-all ${!selection.universityId ? 'opacity-30 pointer-events-none grayscale' : ''}`}>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">TWG Program</label>
                  <select className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={selection.twgProgram} onChange={(e) => updateProgramSelection(idx, 'twgProgram', e.target.value)}>
                    <option value="">Select Program...</option>
                    {applicableTWGProgs.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>

                {/* Destination Course Dropdown */}
                <div className={`space-y-1.5 transition-all ${!selection.twgProgram ? 'opacity-30 pointer-events-none grayscale' : ''}`}>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    Destination Course / Track
                  </label>
                  <select 
                    className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" 
                    value={selection.course || ''} 
                    onChange={(e) => updateProgramSelection(idx, 'course', e.target.value)}
                    /* Disable the dropdown explicitly if no TWG Program is selected */
                    disabled={!selection.twgProgram}
                  >
                    <option value="">Select Course...</option>
                    {applicableCourses.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className={`space-y-1.5 md:col-span-2 transition-all ${!selection.twgProgram ? 'opacity-30 pointer-events-none grayscale' : ''}`}>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">TWG Program Commencement (Intake)</label>
                  <select className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={selection.intake} onChange={(e) => updateProgramSelection(idx, 'intake', e.target.value)}>
                    <option value="">Select Intake...</option>
                    {applicableIntakes.map(date => <option key={date} value={date}>{date}</option>)}
                  </select>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-end gap-4 pt-4">
        <button onClick={() => setActiveSection(2)} className="px-8 py-4 text-xs font-black text-slate-400 uppercase hover:text-slate-800">Back</button>
        <button onClick={() => saveDraft(4)} className="bg-[#0464AD] text-white px-10 py-4 rounded-2xl text-xs font-black uppercase shadow-xl hover:bg-[#0091D0] transition-all">Save & Continue</button>
      </div>
    </div>
  );

  const renderSection4 = () => (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Given Name</label>
          <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.guardianGivenName} onChange={e => setForm({...form, guardianGivenName: e.target.value})} />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Family Name</label>
          <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.guardianFamilyName} onChange={e => setForm({...form, guardianFamilyName: e.target.value})} />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Relationship</label>
          <select className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.guardianRelationship} onChange={e => setForm({...form, guardianRelationship: e.target.value})}>
            <option value="">Select Relationship...</option>
            <option value="Father">Father</option>
            <option value="Mother">Mother</option>
            <option value="Brother">Brother</option>
            <option value="Sister">Sister</option>
            <option value="Guardian">Legal Guardian</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Parent/Guardian Occupation</label>
          <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.guardianOccupation} onChange={e => setForm({...form, guardianOccupation: e.target.value})} />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
          <input type="email" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.guardianEmail} onChange={e => setForm({...form, guardianEmail: e.target.value})} />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Phone Number</label>
          <input 
            type="tel" 
            className={`w-full border rounded-2xl p-4 text-sm outline-none focus:ring-2 transition-all ${
              form.guardianPhone && form.guardianPhone === form.phone 
              ? 'bg-red-50 border-red-200 focus:ring-red-500' 
              : 'bg-slate-50 border-slate-200 focus:ring-[#0464AD]'
            }`} 
            value={form.guardianPhone || ''} 
            onChange={e => setForm({...form, guardianPhone: e.target.value})} 
            placeholder="Enter guardian's phone number"
          />
          {form.guardianPhone && form.guardianPhone === form.phone && (
            <p className="text-[10px] text-red-500 font-bold uppercase tracking-tight ml-1">
              ⚠️ Guardian phone cannot be same as applicant phone
            </p>
          )}
        </div>
      </div>

      <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">Residential Address</h4>
          <p className="text-xs text-slate-400 mt-1">Is the parent/guardian address the same as the applicant's address?</p>
        </div>
        <div className="flex bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
          {['Yes', 'No'].map(opt => (
            <button key={opt} type="button" onClick={() => setForm({...form, guardianAddressSame: opt})} className={`px-6 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${form.guardianAddressSame === opt ? 'bg-[#0464AD] text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}>{opt}</button>
          ))}
        </div>
      </div>
      
      {form.guardianAddressSame === 'No' && (
        <div className="space-y-6 animate-in slide-in-from-top-4 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-3 space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Street Address</label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.guardianStreetAddress} onChange={e => setForm({...form, guardianStreetAddress: e.target.value})} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">City</label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.guardianCity} onChange={e => setForm({...form, guardianCity: e.target.value})} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">State</label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.guardianState} onChange={e => setForm({...form, guardianState: e.target.value})} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Postcode</label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.postcode} onChange={e => setForm({...form, postcode: e.target.value})} />
            </div>
            <div className="lg:col-span-2 space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Country</label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.guardianCountry} onChange={e => setForm({...form, guardianCountry: e.target.value})} />
            </div>
          </div>
        </div>
      )}
      
      <div className="flex justify-end gap-4 pt-4">
        <button type="button" onClick={() => setActiveSection(3)} className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-800">Back</button>
        <button 
          type="button"
          onClick={() => saveDraft(5)} 
          disabled={form.guardianPhone && form.guardianPhone === form.phone}
          className={`px-10 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl transition-all active:scale-95 ${
            form.guardianPhone && form.guardianPhone === form.phone 
            ? 'bg-slate-200 text-slate-400 cursor-not-allowed' 
            : 'bg-[#0464AD] text-white shadow-[#0464AD]/20 hover:bg-[#0091D0]'
          }`}
        >
          Save & Continue
        </button>
      </div>
    </div>
  );

  const renderSection5 = () => (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter leading-none">Academic Qualifications</h3>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Please provide details for up to 3 qualifications.</p>
        </div>
      </div>

      <div className="space-y-8">
        {form.qualifications.map((qual, idx) => (
          <React.Fragment key={idx}>
            <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 relative animate-in slide-in-from-left-4">
              <div className="absolute top-6 right-8 flex items-center gap-4">
                <span className="text-2xl font-black text-[#0464AD] uppercase tracking-tighter">Qualification #{idx + 1}</span>
                {idx > 0 && (
                  <button onClick={() => removeQualification(idx)} className="text-red-400 hover:text-red-600 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeWidth="2.5"/></svg>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Qualification Level</label>
                  <select 
                    className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" 
                    value={qual.highestQualification} 
                    onChange={e => updateQualification(idx, 'highestQualification', e.target.value)}
                  >
                    <option value="">Select Qualification...</option>
                    {(settings?.qualifications || ['High School / Class XII', 'Undergraduate Degree', 'Postgraduate Degree']).map(q => (
                      <option key={q} value={q}>{q}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Education Board / University</label>
                  <input type="text" className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none" value={qual.educationBoard} onChange={e => updateQualification(idx, 'educationBoard', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Country of Completion</label>
                  <input type="text" className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none" value={qual.highestQualificationCountry} onChange={e => updateQualification(idx, 'highestQualificationCountry', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Name of School / Institution</label>
                  <input type="text" className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none" value={qual.institutionName} onChange={e => updateQualification(idx, 'institutionName', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Language of Instruction</label>
                  <input type="text" className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none" value={qual.languageOfInstruction} onChange={e => updateQualification(idx, 'languageOfInstruction', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Status</label>
                  <div className="flex bg-white border border-slate-200 rounded-xl p-1 shadow-sm w-fit">
                    {['Yes', 'No'].map(opt => (
                      <button key={opt} type="button" onClick={() => updateQualification(idx, 'isCompleted', opt)} className={`px-6 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${qual.isCompleted === opt ? 'bg-[#0464AD] text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}>{opt === 'Yes' ? 'Completed' : 'Ongoing'}</button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Date of Completion / Expected</label>
                  <input type="date" className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={qual.completionDate} onChange={e => updateQualification(idx, 'completionDate', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Marks / GPA</label>
                  <input type="text" className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={qual.marksGpa} onChange={e => updateQualification(idx, 'marksGpa', e.target.value)} />
                </div>
              </div>
            </div>
            {idx === form.qualifications.length - 1 && form.qualifications.length < 3 && (
              <div className="mt-4 flex justify-start">
                <button 
                  onClick={addQualification} 
                  className="bg-slate-900 text-white px-6 py-3 rounded-2xl text-[10px] font-black uppercase shadow-lg hover:bg-[#0464AD] transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
                  Add Another Qualification
                </button>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="space-y-4 mt-12">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Academic Documents</label>
        <p className="text-xs text-slate-400 italic">Please upload transcripts and certificates from your listed qualifications.</p>
        <div className="relative group">
          <input type="file" className="hidden" id="academic-docs" multiple onChange={e => setForm({...form, academicDocuments: e.target.files})} />
          <label htmlFor="academic-docs" className="flex items-center justify-between w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm cursor-pointer hover:border-[#0464AD] transition-all">
            <span className="text-slate-500 truncate">{form.academicDocuments ? `${form.academicDocuments.length} files selected` : 'Select Transcripts/Certificates...'}</span>
            <svg className="w-5 h-5 text-slate-300 group-hover:text-[#0464AD]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
          </label>
        </div>
      </div>
      <div className="h-px bg-slate-50" />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">Academic Gap</h4>
            <p className="text-xs text-slate-400 mt-1">Do you have an academic gap of more than 6 months?</p>
          </div>
          <div className="flex bg-slate-50 border border-slate-200 rounded-xl p-1 shadow-sm">
            {['Yes', 'No'].map(opt => (
              <button key={opt} type="button" onClick={() => setForm({...form, hasAcademicGap: opt})} className={`px-6 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${form.hasAcademicGap === opt ? 'bg-[#0464AD] text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}>{opt}</button>
            ))}
          </div>
        </div>
        {form.hasAcademicGap === 'Yes' && (
          <div className="space-y-6 animate-in slide-in-from-top-4 duration-500 bg-slate-50 p-6 rounded-[2rem] border border-slate-100">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Reason for Gap</label>
              <textarea rows="3" className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] resize-none" value={form.gapReason} onChange={e => setForm({...form, gapReason: e.target.value})} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Gap Justification Documents</label>
              <div className="relative group">
                <input type="file" className="hidden" id="gap-docs" multiple onChange={e => setForm({...form, gapDocuments: e.target.files})} />
                <label htmlFor="gap-docs" className="flex items-center justify-between w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm cursor-pointer hover:border-[#0464AD] transition-all">
                  <span className="text-slate-500 truncate">{form.gapDocuments ? `${form.gapDocuments.length} files selected` : 'Upload justification documents...'}</span>
                  <svg className="w-5 h-5 text-slate-300 group-hover:text-[#0464AD]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-4 pt-4">
        <button onClick={() => setActiveSection(4)} className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-800">Back</button>
        <button onClick={() => saveDraft(6)} className="bg-[#0464AD] text-white px-10 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-[#0464AD]/20 hover:bg-[#0091D0] transition-all active:scale-95">Save & Continue</button>
      </div>
    </div>
  );

  const renderSection6 = () => (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 prose prose-slate max-w-none">
        <h4 className="text-sm font-black text-[#0464AD] uppercase tracking-widest mb-4">Verification Guidelines</h4>
        <p className="text-xs text-slate-600 font-medium leading-relaxed mb-6">Please provide accurate details of your English proficiency.</p>
        <div className="space-y-2 mb-6">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Document(s) Required</p>
          <ul className="text-xs text-slate-500 list-disc pl-4 space-y-1">
            <li>Certified* Copy of a standardised English test (e.g. IELTS, TOEFL) OR</li>
            <li>Certified* Copy of final year mark sheet showing English scores OR</li>
            <li>Evidence of tertiary education in English</li>
          </ul>
        </div>
      </div>
      <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">Standardised Testing</h4>
          <p className="text-xs text-slate-400 mt-1">Have you taken an English Proficiency test?</p>
        </div>
        <div className="flex bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
          {['Yes', 'No'].map(opt => (
            <button key={opt} type="button" onClick={() => setForm({...form, hasEnglishTest: opt})} className={`px-6 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${form.hasEnglishTest === opt ? 'bg-[#0464AD] text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}>{opt}</button>
          ))}
        </div>
      </div>
      {form.hasEnglishTest === 'Yes' ? (
        <div className="space-y-6 animate-in slide-in-from-top-4 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">English Test Taken</label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.englishTestName} onChange={e => setForm({...form, englishTestName: e.target.value})} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">English Test Score</label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.englishTestScore} onChange={e => setForm({...form, englishTestScore: e.target.value})} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Verification ID</label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.englishTestVerificationId} onChange={e => setForm({...form, englishTestVerificationId: e.target.value})} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Upload English Test Result</label>
            <div className="relative group">
              <input type="file" className="hidden" id="english-test-doc" multiple onChange={e => setForm({...form, englishTestResultDoc: e.target.files})} />
              <label htmlFor="english-test-doc" className="flex items-center justify-between w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm cursor-pointer hover:border-[#0464AD] transition-all">
                <span className="text-slate-500 truncate">{form.englishTestResultDoc ? `${form.englishTestResultDoc.length} files selected` : 'Select Result Document...'}</span>
                <svg className="w-5 h-5 text-slate-300 group-hover:text-[#0464AD]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
              </label>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6 animate-in slide-in-from-top-4 duration-500">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">English Proficiency Description</label>
            <select className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.englishProficiencyDescription} onChange={e => setForm({...form, englishProficiencyDescription: e.target.value})}>
              <option value="">Select description...</option>
              <option value="Good">Good</option>
              <option value="Bad">Bad</option>
              <option value="Awaiting Result">Awaiting Result / Scheduled</option>
            </select>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-4 pt-4">
        <button onClick={() => setActiveSection(5)} className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-800">Back</button>
        <button onClick={() => saveDraft(7)} className="bg-[#0464AD] text-white px-10 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-[#0464AD]/20 hover:bg-[#0091D0] transition-all active:scale-95">Save & Continue</button>
      </div>
    </div>
  );

  const renderSection7 = () => (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="grid grid-cols-1 gap-8">
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Who is going to be the primary sponsor of your overseas studies?</label>
          <select className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.primarySponsor} onChange={e => setForm({...form, primarySponsor: e.target.value})}>
            <option value="">Select an option...</option>
            <option value="Parents/Sibling/Spouse">Parents/Sibling/Spouse</option>
            <option value="Grandparents">Grandparents</option>
            <option value="Other relatives">Other relatives</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Annual income of your primary sponsor?</label>
          <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.sponsorAnnualIncome} onChange={e => setForm({...form, sponsorAnnualIncome: e.target.value})} placeholder='Mention "0" if not applicable' />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Planned student education loan amount (INR)?</label>
          <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.plannedLoanAmount} onChange={e => setForm({...form, plannedLoanAmount: e.target.value})} placeholder='Mention "0" if not applicable' />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Liquid funds available (Savings/Fixed Deposit)?</label>
          <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.liquidFundsAmount} onChange={e => setForm({...form, liquidFundsAmount: e.target.value})} placeholder='Mention "0" if not applicable' />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Current value of immovable assets (Property etc.)?</label>
          <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" value={form.assetValue} onChange={e => setForm({...form, assetValue: e.target.value})} placeholder='Mention "0" if not applicable' />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Any other relevant funding details?</label>
          <textarea rows="3" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] resize-none" value={form.fundingAdditionalDetails} onChange={e => setForm({...form, fundingAdditionalDetails: e.target.value})} />
        </div>
        <div className="space-y-4">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Financial Documents</label>
          <div className="relative group">
            <input type="file" className="hidden" id="financial-docs" multiple onChange={e => setForm({...form, relevantFinancialDocs: e.target.files})} />
            <label htmlFor="financial-docs" className="flex items-center justify-between w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm cursor-pointer hover:border-[#0464AD] transition-all">
              <span className="text-slate-500 truncate">{form.relevantFinancialDocs ? `${form.relevantFinancialDocs.length} files selected` : 'Select Financial Documents...'}</span>
              <svg className="w-5 h-5 text-slate-300 group-hover:text-[#0464AD]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
            </label>
          </div>
        </div>
      </div>
      <div className="p-8 bg-slate-900 rounded-[2rem] text-white space-y-6">
        <div className="flex items-start gap-4">
          <input type="checkbox" id="financial-declaration" className="w-6 h-6 rounded border-slate-700 text-[#0464AD] focus:ring-[#0464AD] mt-1 cursor-pointer" checked={form.financialDeclaration} onChange={e => setForm({...form, financialDeclaration: e.target.checked})} />
          <label htmlFor="financial-declaration" className="text-xs text-slate-300 leading-relaxed cursor-pointer select-none">I understand that admission and progression is conditional to visa reception. I responsible for demonstrating financial capability (approx. INR 30-40L) during the process.</label>
        </div>
      </div>
      <div className="flex justify-end gap-4 pt-4">
        <button onClick={() => setActiveSection(6)} className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-800">Back</button>
        <button onClick={() => saveDraft(8)} className="bg-[#0464AD] text-white px-10 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-[#0464AD]/20 hover:bg-[#0091D0] transition-all active:scale-95">Save & Continue</button>
      </div>
    </div>
  );

  const renderSection8 = () => {
    const hasIssues = [
      form.visaRefusal, 
      form.criminalHistory, 
      form.nonCompliantRecord, 
      form.overstayRecord, 
      form.relativesInCountry
    ].some(val => val === 'Yes');

    return (
      <div className="space-y-10 animate-in fade-in duration-500">
        <div className="space-y-6">
          {[
            { key: 'visaRefusal', label: 'Do you have any previous visa refusal/s of any country (including current destination applied for)?' },
            { key: 'criminalHistory', label: 'Do you have any previous criminal history or any pending cases in any country (civil/criminal)?' },
            { key: 'nonCompliantRecord', label: 'Do you have any previous non-compliant record, pending tuition fee or unsettled financial transactions?' },
            { key: 'overstayRecord', label: 'Do you have any record of overstay in any country / previous exclusion, expulsion or deportation?' },
            { key: 'relativesInCountry', label: 'Do you have any relatives residing in the country you are applying to study in?' }
          ].map((q) => (
            <div key={q.key} className="p-6 bg-slate-50 rounded-3xl border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <p className="text-sm font-bold text-slate-700 leading-relaxed max-w-2xl">{q.label}</p>
              <div className="flex bg-white border border-slate-200 rounded-xl p-1 shadow-sm h-fit">
                {['Yes', 'No'].map(opt => (
                  <button 
                    key={opt} 
                    type="button" 
                    onClick={() => setForm({...form, [q.key]: opt})} 
                    className={`px-8 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${form[q.key] === opt ? 'bg-[#0464AD] text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {hasIssues && (
          <div className="space-y-4 animate-in slide-in-from-top-4 duration-500 p-8 bg-amber-50 rounded-[2rem] border border-amber-100">
            <label className="text-xs font-black text-amber-800 uppercase tracking-widest flex items-center gap-2">
              ⚠️ Immigration Issue Details (Mandatory)
            </label>
            <textarea 
              required
              rows="4"
              className="w-full bg-white border border-amber-200 rounded-2xl p-6 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] resize-none font-medium text-slate-700"
              placeholder="Please explain and provide all relevant details about the prior immigration issue(s) you have indicated above..."
              value={form.immigrationDetails}
              onChange={e => setForm({...form, immigrationDetails: e.target.value})}
            />
          </div>
        )}

        <div className="flex justify-end gap-4 pt-8">
          <button onClick={() => setActiveSection(7)} className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-800">Back</button>
          <button 
            onClick={() => saveDraft(9)} 
            disabled={hasIssues && !form.immigrationDetails.trim()}
            className={`px-10 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl transition-all active:scale-95 ${
              hasIssues && !form.immigrationDetails.trim() 
              ? 'bg-slate-200 text-slate-400 cursor-not-allowed' 
              : 'bg-[#0464AD] text-white shadow-[#0464AD]/20 hover:bg-[#0091D0]'
            }`}
          >
            Save & Continue
          </button>
        </div>
      </div>
    );
  };

  const renderSection9 = () => (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 prose prose-slate max-w-none">
        <h4 className="text-sm font-black text-[#0464AD] uppercase tracking-widest mb-4">Final Submission Documents</h4>
        <div className="text-xs text-slate-600 font-medium space-y-2">
          <p>Please follow the below general instructions when uploading documents:</p>
          <ul className="list-disc pl-4">
            <li>Ensure that the documents are clearly scanned, and all the information is clearly legible.</li>
            <li>If documents are available in any other language than English, they should be accompanied with a certified translation.</li>
            <li>Please only upload files that are in PDF, JPEG or PNG format with file size &lt; 2MB</li>
          </ul>
        </div>
      </div>
      <div className="space-y-8">
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">1. Personal Documents</h4>
            <p className="text-xs text-slate-400 mt-1">Please upload your ID proof to help us verify your identity (Photograph & Passport/Gov ID).</p>
          </div>
          <div className="relative group">
            <input type="file" className="hidden" id="personal-docs" multiple onChange={e => setForm({...form, section8PersonalDocs: e.target.files})} />
            <label htmlFor="personal-docs" className="flex items-center justify-between w-full bg-slate-50 border border-slate-200 rounded-2xl p-6 text-sm cursor-pointer hover:border-[#0464AD] transition-all">
              <span className="text-slate-500 truncate">{form.section8PersonalDocs ? `${form.section8PersonalDocs.length} files selected` : 'Select Identification Files...'}</span>
              <svg className="w-6 h-6 text-slate-300 group-hover:text-[#0464AD]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
            </label>
          </div>
        </div>
        <div className="h-px bg-slate-50" />
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">2. Other Admission Related Documents</h4>
            <p className="text-xs text-slate-400 mt-1">Provide CV, SOP, Letters of Recommendation, experience letters, or any other relevant files.</p>
          </div>
          <div className="relative group">
            <input type="file" className="hidden" id="admission-docs" multiple onChange={e => setForm({...form, section8AdmissionDocs: e.target.files})} />
            <label htmlFor="admission-docs" className="flex items-center justify-between w-full bg-slate-50 border border-slate-200 rounded-2xl p-6 text-sm cursor-pointer hover:border-[#0464AD] transition-all">
              <span className="text-slate-500 truncate">{form.section8AdmissionDocs ? `${form.section8AdmissionDocs.length} files selected` : 'Select Admission Files...'}</span>
              <svg className="w-6 h-6 text-slate-300 group-hover:text-[#0464AD]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
            </label>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-4 pt-8">
        <button onClick={() => setActiveSection(8)} className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-800 transition-colors">Back</button>
        <button onClick={() => saveDraft(10)} className="bg-[#0464AD] text-white px-10 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-[#0464AD]/20 hover:bg-[#0091D0] transition-all active:scale-95">Save & Continue</button>
      </div>
    </div>
  );

  const renderSection10 = () => {
    // --- DEFINE VALIDATION HELPERS WITHIN SCOPE ---
    
    // Section 3 Check: Ensure first preference has all university and program details
    const firstPref = form.programSelections[0];
    const isSection3Complete = !!(
      firstPref?.country && 
      firstPref?.universityId && 
      firstPref?.twgProgram && 
      firstPref?.course && 
      firstPref?.intake
    );

    // Section 4 Check: Ensure mandatory guardian details are provided
    const isSection4Complete = !!(
      form.guardianGivenName?.trim() && 
      form.guardianFamilyName?.trim() && 
      form.guardianRelationship && 
      form.guardianOccupation?.trim() && 
      form.guardianEmail?.trim() && 
      form.guardianPhone?.trim() &&
      form.guardianPhone !== form.phone // Enforce unique phone number requirement
    );

    // Section 5 Check: Ensure Qualification #1 is fully filled out
    const firstQual = form.qualifications[0];
    const isSection5Complete = !!(
      firstQual?.highestQualification && 
      firstQual?.educationBoard?.trim() && 
      firstQual?.highestQualificationCountry?.trim() && 
      firstQual?.institutionName?.trim() && 
      firstQual?.languageOfInstruction?.trim() && 
      firstQual?.completionDate && 
      firstQual?.marksGpa?.trim()
    );

    // Section 7 Check: Ensure all mandatory financial assessments are completed
    const isSection7Complete = !!(
      form.primarySponsor && 
      form.sponsorAnnualIncome?.trim() !== "" && 
      form.plannedLoanAmount?.trim() !== "" && 
      form.liquidFundsAmount?.trim() !== "" && 
      form.assetValue?.trim() !== "" &&
      form.financialDeclaration === true
    );

    return (
      <div className="space-y-10 animate-in fade-in duration-500 pb-20">
        <div className="space-y-8">
          <div className="space-y-6">
            <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight leading-none">1. Declaration</h4>
            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 text-xs text-slate-600 leading-relaxed space-y-4">
              <p>I declare the information I have supplied on this form is, to the best of my understanding and belief, complete and correct.</p>
              <ul className="list-disc pl-5 space-y-3">
                <li>I understand that giving fraudulent, false or incomplete information may lead to my application being refused or my enrolment cancelled. I will notify The WorldGrad immediately if there is any change to the information I have given in this application.</li>
                <li>I have read and understood the relevant program information in the The WorldGrad brochure and/or on TheWorldGrad website and I have sufficient information about the programs to enrol.</li>
                <li>I understand that the programs may lead to future studies at another Institution, subject to their entry requirements.</li>
                <li>I understand that if I have applied through an approved The WorldGrad agent, all correspondence relating to my application will be forwarded to that agent.</li>
                <li>I understand that fees may increase. I accept liability for payment of all fees, and I agree to abide by the Student Fees and Refund Policy.</li>
                <li>I have read and understood the Enrolment Policy as specified on the The WorldGrad website <a href="http://www.theworldgrad.com" className="text-[#0464AD] hover:underline" target="_blank" rel="noopener noreferrer">www.theworldgrad.com</a>. I understand that The WorldGrad may, vary its conditions as may be necessary to comply with any law or regulation, or amendment of any law or regulation.</li>
                <li>I give permission for The WorldGrad to obtain official records from an educational institution attended by me for the purpose of verifying the supporting documentation I have provided with my application, and to supply my contact details and any relevant official records to educational institutions (including ELICOS providers) to which I am eligible for admission.</li>
                <li>I understand that any conditions concerning an offer of admission will be contained in my letter of offer from TheWorldGrad, which I will be required to read and sign.</li>
              </ul>
            </div>
            <div className="flex items-center gap-3 px-2">
              <input 
                type="checkbox" 
                id="decl-agree" 
                className="w-5 h-5 rounded border-slate-300 text-[#0464AD] focus:ring-[#0464AD] cursor-pointer" 
                checked={form.declarationAgree} 
                onChange={e => setForm({...form, declarationAgree: e.target.checked})} 
              />
              <label htmlFor="decl-agree" className="text-xs font-bold text-slate-700 cursor-pointer select-none">I Agree to the Declaration</label>
            </div>
          </div>

          <div className="h-px bg-slate-50" />

          <div className="space-y-6">
            <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight leading-none">2. Privacy Declaration</h4>
            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 text-xs text-slate-600 leading-relaxed space-y-4">
              <p>Our Privacy Policy, outlines how the information you provide us, will allow us to deal with your enquiry; to assess your application and, provide you with an outcome.</p>
              <p className="mt-2 font-bold italic">By clicking "I accept" you acknowledge that:</p>
              <ul className="list-disc pl-5 space-y-3 mt-3">
                <li>You have read and understood The WorldGrad's Privacy Policy</li>
                <li>You consent to the college sending you marketing information</li>
                <li>You consent to The WorldGrad contacting me by telephone for marketing purposes</li>
                <li>You understand that The WorldGrad will be collecting, processing and storing your personal information as part of this enquiry and/or application process</li>
                <li>You understand that The WorldGrad may need to share your personal information with a third party in order to provide its services to you and you agree to this</li>
                <li>You understand that as part of your learning experience, digital recordings, verbal and visual, will be made of class sessions being conducted as part of the digital learning environment; you consent to be being part of the recording process</li>
                <li>You understand that you can opt out of a recorded session by not attending at all; muting your microphone; and/or turning off your camera</li>
                <li>If you decide to opt out of participation in recorded sessions by not attending, muting your microphone and/or turning off your camera, you understand that this may have a negative impact on your learning experience by restricting interaction with your teacher/s and classmates</li>
                <li>These recordings will be stored in a secure system accessible via a password that will be provided to you for your use, you understand that if you download and share these recordings you will be in breach of the Policy and therefore subject to disciplinary action</li>
                <li>You also confirm that you have read and understood the Live Recording of Course Work Policy</li>
                <li>You grant The WorldGrad permission to provide your parent or guardian referenced in this form with any information pertaining to your application to study, ongoing academic progress, results and attendance</li>
              </ul>
            </div>
            <div className="flex items-center gap-3 px-2">
              <input 
                type="checkbox" 
                id="priv-agree" 
                className="w-5 h-5 rounded border-slate-300 text-[#0464AD] focus:ring-[#0464AD] cursor-pointer" 
                checked={form.privacyAgree} 
                onChange={e => setForm({...form, privacyAgree: e.target.checked})} 
              />
              <label htmlFor="priv-agree" className="text-xs font-bold text-slate-700 cursor-pointer select-none">I Accept the Privacy Policy</label>
            </div>
          </div>

          <div className="h-px bg-slate-50" />

          <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-700">3. Is the student above 18 years of age?</p>
              <div className="flex bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
                {['Yes', 'No'].map(opt => (
                  <button 
                    key={opt} 
                    type="button"
                    onClick={() => setForm({...form, isAbove18: opt})} 
                    className={`px-8 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                      form.isAbove18 === opt ? 'bg-[#0464AD] text-white shadow-md' : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            {form.isAbove18 === 'No' && (
              <div className="space-y-1.5 animate-in slide-in-from-top-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">4. Parent / Guardian Signature (Enter Full Name)</label>
                <input 
                  type="text" 
                  placeholder="Type full legal name as signature..."
                  className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" 
                  value={form.guardianSignature} 
                  onChange={e => setForm({...form, guardianSignature: e.target.value})} 
                />
                <p className="text-[9px] text-slate-400 font-medium ml-1">Since the student is under 18, a parent or legal guardian must provide their name as an electronic signature.</p>
              </div>
            )}
          </div>

          <div className="h-px bg-slate-50" />

          <div className="space-y-6">
            <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight leading-none">5. Recruitment Partner Declaration</h4>
            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 text-xs text-slate-600 leading-relaxed italic">
              <p>I confirm that I have verified the authenticity of the documents provided and that the applicant is eligible for the chosen programs based on the entry requirements.</p>
            </div>
            <div className="flex items-center gap-3 px-2">
              <input 
                type="checkbox" 
                id="partner-agree" 
                className="w-5 h-5 rounded border-slate-300 text-[#0464AD] focus:ring-[#0464AD] cursor-pointer" 
                checked={form.partnerDeclarationAgree} 
                onChange={e => setForm({...form, partnerDeclarationAgree: e.target.checked})} 
              />
              <label htmlFor="partner-agree" className="text-xs font-bold text-slate-700 cursor-pointer select-none">I Agree as an Authorised Recruitment Partner</label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 pt-8">
          <button type="button" onClick={() => setActiveSection(9)} className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-800 transition-colors">Back</button>
          
          {!viewOnly && (
            <button 
              onClick={handleSubmit}
              disabled={isSubmitting} // Only disabled while the "Uploading" state is active
              className={`px-10 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl transition-all active:scale-95 ${
                isSubmitting 
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed' 
                : 'bg-[#0464AD] text-white shadow-[#0464AD]/20 hover:bg-[#0091D0]'
              }`}
            >
              {isSubmitting ? 'Uploading & Submitting...' : 'Submit Final Application'}
            </button>
          )}

        </div>
      </div>
    );
  };

  const renderCreateView = () => (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 h-full flex flex-col">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => setView('list')} className="w-10 h-10 bg-white rounded-xl shadow-sm border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0464AD] transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
        </button>
        
        <div className="flex-1 overflow-x-hidden py-1">
          <div className="flex gap-1.5 justify-between">
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center justify-center gap-1.5 px-1.5 py-2 rounded-xl transition-all border flex-1 min-w-0 ${
                  activeSection === section.id 
                  ? 'bg-[#0464AD] text-white shadow-lg shadow-[#0464AD]/20 border-[#0464AD]' 
                  : 'bg-white text-slate-400 hover:bg-slate-50 border-slate-100'
                }`}
              >
                <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-black shrink-0 ${
                  activeSection === section.id ? 'bg-white/20' : 'bg-slate-100'
                }`}>
                  {section.id}
                </div>
                <span className="text-[9px] font-black uppercase tracking-tight truncate">
                  {section.title}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10 min-h-[600px] flex-1 overflow-y-auto">
          {activeSection === 1 && renderSection1()}
          {activeSection === 2 && renderSection2()}
          {activeSection === 3 && renderSection3()}
          {activeSection === 4 && renderSection4()}
          {activeSection === 5 && renderSection5()}
          {activeSection === 6 && renderSection6()}
          {activeSection === 7 && renderSection7()}
          {activeSection === 8 && renderSection8()}
          {activeSection === 9 && renderSection9()}
          {activeSection === 10 && renderSection10()}
      </div>
    </div>
  );

  const renderListView = () => (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight">University Applications</h3>
        {!viewOnly && (
          <button onClick={() => { setView('create'); setActiveSection(1); }} className="bg-[#0464AD] text-white px-6 py-2.5 rounded-xl text-[10px] font-black uppercase shadow-lg hover:bg-[#0091D0] transition-all">New Application</button>
        )}
      </div>

      {/* --- NEW: Filter Bar UI --- */}
      <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        {/* Applicant Search */}
        <div className="space-y-1.5">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Search Applicant</label>
          <input 
            type="text"
            placeholder="Name or ID..."
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-bold outline-none focus:ring-2 focus:ring-[#0464AD]"
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
          />
        </div>

        {/* Program Filter */}
        <div className="space-y-1.5">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Program</label>
          <select 
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-bold outline-none cursor-pointer"
            value={filters.program}
            onChange={(e) => setFilters({...filters, program: e.target.value})}
          >
            <option value="All Programs">All Programs</option>
            {(settings?.twgPrograms || []).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {/* Intake Filter */}
        <div className="space-y-1.5">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Intake</label>
          <select 
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-bold outline-none cursor-pointer"
            value={filters.intake}
            onChange={(e) => setFilters({...filters, intake: e.target.value})}
          >
            <option value="All Intakes">All Intakes</option>
            {/* Extracts unique intakes currently present in your applications list */}
            {[...new Set(applications.map(a => a.programSelections?.[0]?.intake || a.intakeSummary).filter(Boolean))].map(intake => (
              <option key={intake} value={intake}>{intake}</option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div className="space-y-1.5">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Status</label>
          <select 
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-bold outline-none cursor-pointer"
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
          >
            <option value="All Statuses">All Statuses</option>
            {APPLICATION_STATUSES.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        {/* Reset Action */}
        <div className="flex items-end pb-0.5">
          <button 
            onClick={() => setFilters({ program: 'All Programs', intake: 'All Intakes', status: 'All Statuses', search: '' })}
            className="text-[9px] font-black text-slate-400 hover:text-red-500 uppercase tracking-widest transition-colors mb-2 ml-2"
          >
            Reset Filters
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Applicant Name</th>
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date of Birth</th>
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Program Applied For</th>
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Intake</th>
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date Applied</th>
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
              <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {/* UPDATED: Mapping over filteredApplications instead of applications */}
            {filteredApplications.map((app) => (
              <tr key={app.id} className="hover:bg-slate-50/50 transition-colors group">
                <td className="px-8 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-slate-800">{app.applicantName}</span>
                    <span className="text-[10px] font-black text-[#0464AD] uppercase tracking-tighter">{app.id}</span>
                  </div>
                </td>
                <td className="px-8 py-4">
                  <span className="text-sm font-medium text-slate-600">
                    {(() => {
                      const rawDob = app.dob;
                      if (!rawDob) return '—';

                      // Convert the stored string (YYYY-MM-DD) into a formatted Date object
                      const dateObj = new Date(rawDob);
                      
                      // Check if the date is valid before formatting
                      if (isNaN(dateObj.getTime())) return rawDob;

                      return dateObj.toLocaleDateString('en-GB', { 
                        day: '2-digit', 
                        month: 'short', 
                        year: 'numeric' 
                      });
                    })() || '—'}
                  </span>
                </td>
                <td className="px-8 py-4">
                  <span className="text-sm text-slate-600 font-medium">{app.programSelections?.[0]?.twgProgram || app.programSummary || '—'}</span>
                </td>
                <td className="px-8 py-4">
                  <span className="text-sm text-slate-600 font-medium">{app.programSelections?.[0]?.intake || app.intakeSummary || '—'}</span>
                </td>
                <td className="px-8 py-4">
                  <span className="text-sm font-medium text-slate-600">
                    {(() => {
                      // 1. Get the raw date value from either field
                      const rawDate = app.dateApplied || app.createdAt;
                      if (!rawDate) return '—';

                      // 2. Convert to a Date object (handles both string and Firestore Timestamp)
                      const dateObj = rawDate.seconds 
                        ? new Date(rawDate.seconds * 1000) 
                        : new Date(rawDate);

                      // 3. Format to "18 Feb 2026"
                      return dateObj.toLocaleDateString('en-GB', { 
                        day: '2-digit', 
                        month: 'short', 
                        year: 'numeric' 
                      });
                    })()}
                  </span>
                </td>
                <td className="px-8 py-4 text-center">
                  <span className="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-500">{app.status}</span>
                </td>
                <td className="px-8 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    {/* RESUME DRAFT BUTTON */}
                    {app.status === 'Draft' ? (
                      !viewOnly && (
                        <button 
                          onClick={() => {
                            setForm(app);           // Load saved draft data into the form
                            setCurrentDraftId(app.id); // Track this ID for future saves
                            setView('create');       // Switch to the creation form view
                            setActiveSection(2);     // Jump straight to the details section
                          }}
                          className="px-4 py-1.5 bg-amber-500 text-white text-[10px] font-black uppercase rounded-lg shadow-sm hover:bg-amber-600 transition-all active:scale-95"
                          title="Resume Draft"
                        >
                          Resume
                        </button>
                      )
                    ) : (
                      /* VIEW COMPLETED APPLICATION BUTTON */
                      <button 
                        onClick={() => setViewingApp(app)}
                        className="p-2 text-[#0464AD] hover:bg-blue-50 rounded-xl transition-all"
                        title="View Full Application"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {/* Optional: Show Empty State if no results match filters */}
            {filteredApplications.length === 0 && (
              <tr>
                <td colSpan="7" className="px-8 py-12 text-center text-slate-400 italic text-sm">
                  No applications match your selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {viewingApp && (
        <ApplicationSummary 
          application={viewingApp} 
          settings={settings} 
          onClose={() => setViewingApp(null)} 
        />
      )}
    </div>
  );

  return view === 'list' ? renderListView() : renderCreateView();
};

export default Applications;